import { Button } from '@patternfly/react-core';
import { LanguageIcon } from '@patternfly/react-icons';
import * as React from 'react';

import { TranslatedComment, hasTranslation } from '@app/model/Survey';

export interface TranslatableTextProps {
  /** The comment and the English of it, as the service holds them. */
  comment: TranslatedComment;
  /**
   * Drawn on the control line ahead of the switch, for a caller that already labels the text it is
   * showing - the comment box's name, on a respondent's card.
   */
  prefix?: React.ReactNode;
  /**
   * Names this particular comment in the switch's accessible label, so a page carrying a dozen of
   * them does not offer a dozen buttons all called the same thing.
   */
  what?: string;
}

/**
 * A student's comment, with a switch between what they wrote and the English of it.
 *
 * **Nothing here goes to the network.** Both versions arrive on the comment: the service translates
 * a class's comments once, when somebody asks it to from the survey page, and stores the English
 * beside the original. Switching is therefore free and the same for every reader, which is the
 * point of having moved it - the same comment used to be translated again for every person who
 * opened the page.
 *
 * The original is what is shown until asked, because it is the record: a translation is a reading
 * aid, and anybody acting on what a student said should be seeing the words they actually used.
 *
 * The switch is offered only when there are two versions to switch between. A comment nobody has
 * translated yet has one, and so does a comment the student wrote in English - the service stores
 * that back unchanged, so the two fields match and there is nothing to show. Both render as plain
 * text with no control at all rather than as a button that does nothing.
 */
export const TranslatableText: React.FunctionComponent<TranslatableTextProps> = ({
  comment,
  prefix,
  what = 'this comment'
}) => {
  const [showTranslation, setShowTranslation] = React.useState(false);

  const canSwitch = hasTranslation(comment);

  // Both call sites key a comment by its position in a list, so a survey reloaded with different
  // comments - which is exactly what translating the class does - can hand this instance a new
  // pair. Showing one comment's translation over another's words would be worse than showing none.
  React.useEffect(() => {
    setShowTranslation(false);
  }, [comment.original, comment.english]);

  const showing = showTranslation && canSwitch ? comment.english : comment.original;

  return (
    <>
      {(prefix || canSwitch) && (
        <div className="cas-translatable-controls">
          {prefix}
          {canSwitch && (
            <Button
              variant="link"
              isInline
              icon={<LanguageIcon />}
              onClick={() => setShowTranslation(shown => !shown)}
              // The visible label is short because it sits beside the text it belongs to; the
              // spoken one names the comment, or a page of these reads out as a row of identical
              // buttons.
              aria-label={
                showTranslation ? `Show the original of ${what}` : `Show the translation of ${what}`
              }
            >
              {showTranslation ? 'Show original' : 'Show translation'}
            </Button>
          )}
        </div>
      )}
      <p>{showing}</p>
    </>
  );
};
